import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../lib/supabase";
import { useToast } from "../ui/ToastModal";
import { useAuth } from "../../hooks/useAuth";
import {
  Newspaper,
  Save,
  Loader2,
  Send,
  Clock,
  Globe,
  History,
  ChevronDown,
  ChevronUp,
  Zap,
  Check,
  X,
  CalendarDays
} from "lucide-react";
import { logError } from "../../lib/logError";

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- newsletter tables not in generated Database type yet
const db = supabase as any;

type DeliveryFrequency = 'daily' | 'weekdays' | 'weekly' | 'custom';

const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

const FREQUENCY_OPTIONS: { value: DeliveryFrequency; label: string; icon: string; desc: string }[] = [
  { value: 'daily', label: 'Daily', icon: '📅', desc: 'Every day' },
  { value: 'weekdays', label: 'Weekdays', icon: '💼', desc: 'Mon to Fri' },
  { value: 'weekly', label: 'Weekly', icon: '📌', desc: 'One day per week' },
  { value: 'custom', label: 'Custom', icon: '⚙️', desc: 'Pick specific days' },
];

interface UserNewsletterPref {
  id: string;
  user_id: string;
  organization_id: string;
  timezone: string;
  send_hour: number;
  send_minute: number;
  is_enabled: boolean;
  custom_perplexity_topics: string | null;
  delivery_frequency: DeliveryFrequency;
  delivery_days: number[] | null;
  enabled_sections: string[];
}

interface NewsletterSection {
  id: string;
  key: string;
  label: string;
  description: string | null;
  icon: string;
  sort_order: number;
  is_enabled: boolean;
  required_permission: string | null;
}

interface NewsletterLog {
  id: string;
  user_id: string;
  email: string;
  status: string;
  content_mode: string | null;
  sections_included: string[] | null;
  error_message: string | null;
  sent_at: string;
}

const TIMEZONES = [
  "Australia/Brisbane",
  "Australia/Sydney",
  "Australia/Melbourne",
  "Australia/Perth",
  "Australia/Adelaide",
  "Australia/Darwin",
  "Australia/Hobart",
  "Pacific/Auckland",
  "Asia/Singapore",
  "UTC",
];

// Helper Toggle Switch Component
const ToggleSwitch = ({ checked, onChange, disabled = false }: { checked: boolean, onChange: () => void, disabled?: boolean }) => (
  <button
    type="button"
    onClick={onChange}
    disabled={disabled}
    style={{
      width: "44px",
      height: "24px",
      borderRadius: "12px",
      background: checked ? "#10b981" : "#e2e8f0",
      position: "relative",
      border: "none",
      cursor: disabled ? "not-allowed" : "pointer",
      transition: "background 0.3s ease",
      flexShrink: 0,
      opacity: disabled ? 0.6 : 1,
    }}
  >
    <div
      style={{
        position: "absolute",
        top: "2px",
        left: checked ? "22px" : "2px",
        width: "20px",
        height: "20px",
        borderRadius: "50%",
        background: "#ffffff",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        transition: "left 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
      }}
    />
  </button>
);

export function NewsletterSettingsCard() {
  const { profile, user } = useAuth();
  const { addToast } = useToast();

  const [userPref, setUserPref] = useState<UserNewsletterPref | null>(null);
  const [sections, setSections] = useState<NewsletterSection[]>([]);
  const [logs, setLogs] = useState<NewsletterLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);

  // Local form state
  const [timezone, setTimezone] = useState("Australia/Brisbane");
  const [sendHour, setSendHour] = useState(8);
  const [sendMinute, setSendMinute] = useState(0);
  const [isEnabled, setIsEnabled] = useState(true);
  const [customTopics, setCustomTopics] = useState("");
  const [deliveryFrequency, setDeliveryFrequency] = useState<DeliveryFrequency>("daily");
  const [deliveryDays, setDeliveryDays] = useState<number[]>([1]);
  const [enabledSections, setEnabledSections] = useState<string[]>(["activity_summary", "upcoming_deadlines", "industry_news"]);



  const fetchData = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      // Load user's own preferences
      let { data: prefData } = await db
        .from("user_newsletter_preferences")
        .select("*")
        .eq("user_id", user.id)
        .limit(1)
        .single();

      // Auto-enroll: create default preferences if none exist
      if (!prefData) {
        const orgId = (profile as unknown as Record<string, unknown>)?.organization_id as string || "1247c403-664a-4207-ab4d-5591b57d5193";
        const defaultSections = ["activity_summary", "upcoming_deadlines", "industry_news"];
        if (profile?.can_view_financials) defaultSections.push("pnl_snapshot");
        if (profile?.can_view_vault) defaultSections.push("vault_updates");

        const { data: inserted } = await db
          .from("user_newsletter_preferences")
          .insert({
            user_id: user.id,
            organization_id: orgId,
            is_enabled: true,
            timezone: "Australia/Brisbane",
            send_hour: 8,
            send_minute: 0,
            delivery_frequency: "daily",
            delivery_days: null,
            enabled_sections: defaultSections,
          })
          .select()
          .single();
        prefData = inserted;
      }

      if (prefData) {
        const c = prefData as unknown as UserNewsletterPref;
        setUserPref(c);
        setTimezone(c.timezone);
        setSendHour(c.send_hour);
        setSendMinute(c.send_minute);
        setIsEnabled(c.is_enabled);
        setCustomTopics(c.custom_perplexity_topics || "");
        setDeliveryFrequency(c.delivery_frequency || "daily");
        setDeliveryDays(c.delivery_days || [1]);
        setEnabledSections(c.enabled_sections || []);
      }

      // Load master section list (for display purposes)
      const { data: sectionsData } = await db
        .from("newsletter_sections")
        .select("*")
        .order("sort_order", { ascending: true });
      if (sectionsData) setSections(sectionsData as unknown as NewsletterSection[]);

      // Load user's own logs
      const { data: logsData } = await db
        .from("newsletter_logs")
        .select("*")
        .eq("user_id", user.id)
        .order("sent_at", { ascending: false })
        .limit(20);
      if (logsData) setLogs(logsData as unknown as NewsletterLog[]);
    } catch (err) {
      logError("[Newsletter] Load error:", err);
    } finally {
      setLoading(false);
    }
  }, [user?.id, profile]);

  // Separate lightweight logs-only fetch (no skeleton reload)
  const fetchLogs = useCallback(async () => {
    if (!user?.id) return;
    setLogsLoading(true);
    try {
      const { data: logsData } = await db
        .from("newsletter_logs")
        .select("*")
        .eq("user_id", user.id)
        .order("sent_at", { ascending: false })
        .limit(20);
      if (logsData) setLogs(logsData as unknown as NewsletterLog[]);
    } catch (err) {
      logError("[Newsletter] Logs fetch error:", err);
    } finally {
      setLogsLoading(false);
    }
  }, [user?.id]);

  // Dirty-state detection: compare current form values against saved preferences
  const hasChanges = (() => {
    if (!userPref) return false;
    if (timezone !== userPref.timezone) return true;
    if (sendHour !== userPref.send_hour) return true;
    if (sendMinute !== userPref.send_minute) return true;
    if (isEnabled !== userPref.is_enabled) return true;
    if ((customTopics || "") !== (userPref.custom_perplexity_topics || "")) return true;
    if (deliveryFrequency !== (userPref.delivery_frequency || "daily")) return true;
    // Compare delivery days arrays
    const savedDays = userPref.delivery_days || [1];
    if (JSON.stringify([...deliveryDays].sort()) !== JSON.stringify([...savedDays].sort())) return true;
    // Compare enabled sections arrays
    if (JSON.stringify([...enabledSections].sort()) !== JSON.stringify([...(userPref.enabled_sections || [])].sort())) return true;
    return false;
  })();

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async () => {
    if (!userPref) return;
    
    if (customTopics && customTopics.trim().length > 0 && customTopics.trim().length < 10) {
      addToast({ type: "error", title: "Please provide a more detailed AI research directive (min 10 characters), or leave it empty." });
      return;
    }

    setSaving(true);
    try {
      if (deliveryFrequency === 'weekly' && deliveryDays.length !== 1) {
        addToast({ type: "error", title: "Weekly schedule requires exactly one day selected." });
        setSaving(false);
        return;
      }
      if (deliveryFrequency === 'custom' && deliveryDays.length === 0) {
        addToast({ type: "error", title: "Custom schedule requires at least one day selected." });
        setSaving(false);
        return;
      }

      const updatePayload = {
          timezone,
          send_hour: sendHour,
          send_minute: sendMinute,
          is_enabled: isEnabled,
          custom_perplexity_topics: customTopics || null,
          delivery_frequency: deliveryFrequency,
          delivery_days: (deliveryFrequency === 'daily' || deliveryFrequency === 'weekdays') ? null : deliveryDays,
          enabled_sections: enabledSections,
          updated_at: new Date().toISOString(),
      };
      
      const { error } = await db
        .from("user_newsletter_preferences")
        .update(updatePayload)
        .eq("id", userPref.id)
        .select();

      if (error) throw error;
      // Sync userPref to reflect the newly saved state (resets dirty flag)
      setUserPref((prev) => prev ? {
        ...prev,
        timezone,
        send_hour: sendHour,
        send_minute: sendMinute,
        is_enabled: isEnabled,
        custom_perplexity_topics: customTopics || null,
        delivery_frequency: deliveryFrequency,
        delivery_days: (deliveryFrequency === 'daily' || deliveryFrequency === 'weekdays') ? null : deliveryDays,
        enabled_sections: enabledSections,
      } : prev);
      addToast({ type: "success", title: "Your newsletter settings saved." });
    } catch (err) {
      addToast({
        type: "error",
        title: `Save failed: ${err instanceof Error ? err.message : String(err)}`,
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (sectionKey: string) => {
    // Per-user toggle: updates local state only, saved with handleSave
    setEnabledSections((prev) =>
      prev.includes(sectionKey)
        ? prev.filter((k) => k !== sectionKey)
        : [...prev, sectionKey]
    );
  };

  const sendTest = async () => {
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "daily-newsletter",
        {
          body: {
            testMode: true,
            userId: user?.id,
            originUrl: window.location.origin,
          },
        }
      );
      if (error) throw error;
      addToast({
        type: "success",
        title: `Test newsletter sent to you only! ${data?.contentMode === "monthly_summary" ? "(Monthly summary)" : "(Period mode)"}`,
      });
      if (showHistory) fetchLogs();
    } catch (err) {
      addToast({
        type: "error",
        title: `Test failed: ${err instanceof Error ? err.message : String(err)}`,
      });
    } finally {
      setSending(false);
    }
  };



  if (loading) {
    return (
      <div 
        className="card"
        style={{
          background: "rgba(255, 255, 255, 0.7)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(255, 255, 255, 0.6)",
          boxShadow: "0 10px 40px -10px rgba(30, 115, 190, 0.08)",
          padding: "32px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "32px" }}>
          <div className="skeleton-circle" style={{ width: "48px", height: "48px" }} />
          <div>
            <div className="skeleton" style={{ width: "200px", height: "24px", marginBottom: "8px" }} />
            <div className="skeleton" style={{ width: "300px", height: "16px" }} />
          </div>
        </div>
        <div className="skeleton" style={{ width: "100%", height: "200px", borderRadius: "12px", marginBottom: "24px" }} />
        <div className="skeleton" style={{ width: "100%", height: "300px", borderRadius: "12px" }} />
      </div>
    );
  }

  return (
    <div 
      className="card"
      style={{
        background: "rgba(255, 255, 255, 0.8)",
        backdropFilter: "blur(12px)",
        border: "1px solid rgba(255, 255, 255, 0.9)",
        boxShadow: "0 10px 40px -10px rgba(30, 115, 190, 0.1)",
        borderRadius: "16px",
        overflow: "hidden",
        padding: "0",
      }}
    >
      {/* Premium Header */}
      <div style={{
        padding: "24px 32px",
        background: "linear-gradient(to right, rgba(240, 247, 255, 0.5), rgba(255, 255, 255, 0.8))",
        borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "20px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <div
            style={{
              padding: "12px",
              background: "linear-gradient(135deg, #3EBBF0 0%, #1e73be 100%)",
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(30, 115, 190, 0.2)",
              color: "#fff",
            }}
          >
            <Newspaper size={24} />
          </div>
          <div>
            <h2 style={{ fontSize: "1.25rem", fontWeight: 700, color: "#0f172a", marginBottom: "4px" }}>
              My Newsletter
            </h2>
            <p style={{ fontSize: "0.875rem", color: "#64748b", margin: 0 }}>
              Configure your personalised AI briefing schedule and content.
            </p>
          </div>
        </div>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <button
            onClick={sendTest}
            disabled={sending}
            className="btn"
            style={{
              background: "rgba(255, 255, 255, 0.9)",
              color: "#3EBBF0",
              border: "1px solid rgba(62, 187, 240, 0.3)",
              boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
              borderRadius: "8px",
              fontWeight: 600,
            }}
          >
            {sending ? <Loader2 className="spinning" size={16} /> : <Send size={16} />}
            <span>{sending ? "Sending..." : "Test Send"}</span>
          </button>

        </div>
      </div>

      <div style={{ padding: "32px", display: "flex", flexDirection: "column", gap: "32px" }}>
        
        {/* Top Controls Grid */}
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", 
          gap: "24px" 
        }}>
          {/* Schedule Panel */}
          <div style={{
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            padding: "20px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "20px" }}>
              <Clock size={18} color="#1e73be" />
              <h3 style={{ fontSize: "1rem", fontWeight: 600, color: "#0f172a", margin: 0 }}>Delivery Schedule</h3>
            </div>
            
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {/* Timezone */}
              <div>
                <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.8rem", fontWeight: 600, color: "#64748b", marginBottom: "8px" }}>
                  <Globe size={14} /> Timezone
                </label>
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="form-input"
                  style={{ background: "#f8fafc" }}
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>

              {/* Frequency Selector */}
              <div>
                <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.8rem", fontWeight: 600, color: "#64748b", marginBottom: "8px" }}>
                  <CalendarDays size={14} /> Frequency
                </label>
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: "4px",
                  background: "#f1f5f9",
                  borderRadius: "10px",
                  padding: "4px",
                }}>
                  {FREQUENCY_OPTIONS.map((opt) => {
                    const isActive = deliveryFrequency === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          setDeliveryFrequency(opt.value);
                          // Set sensible defaults when switching
                          if (opt.value === 'weekly' && deliveryDays.length !== 1) {
                            setDeliveryDays([1]); // Monday
                          }
                          if (opt.value === 'custom' && deliveryDays.length === 0) {
                            setDeliveryDays([1, 3, 5]); // Mon/Wed/Fri
                          }
                        }}
                        style={{
                          padding: "8px 4px",
                          borderRadius: "8px",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "0.75rem",
                          fontWeight: isActive ? 700 : 500,
                          color: isActive ? "#ffffff" : "#475569",
                          background: isActive
                            ? "linear-gradient(135deg, #1e73be 0%, #0E57C4 100%)"
                            : "transparent",
                          boxShadow: isActive ? "0 2px 8px rgba(30, 115, 190, 0.3)" : "none",
                          transition: "all 0.2s ease",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "2px",
                          lineHeight: 1.3,
                        }}
                      >
                        <span style={{ fontSize: "0.85rem" }}>{opt.icon}</span>
                        <span>{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Day Picker: shown for weekly & custom */}
              {(deliveryFrequency === 'weekly' || deliveryFrequency === 'custom') && (
                <div>
                  <label style={{ fontSize: "0.8rem", fontWeight: 600, color: "#64748b", marginBottom: "8px", display: "block" }}>
                    {deliveryFrequency === 'weekly' ? 'Select Day' : 'Select Days'}
                  </label>
                  <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                    {DAY_LABELS.map((dayLabel, idx) => {
                      const isSelected = deliveryDays.includes(idx);
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => {
                            if (deliveryFrequency === 'weekly') {
                              // Single select
                              setDeliveryDays([idx]);
                            } else {
                              // Multi-toggle
                              setDeliveryDays((prev) =>
                                prev.includes(idx)
                                  ? prev.filter((d) => d !== idx)
                                  : [...prev, idx].sort()
                              );
                            }
                          }}
                          title={DAY_NAMES[idx]}
                          style={{
                            width: "38px",
                            height: "38px",
                            borderRadius: "50%",
                            border: isSelected ? "2px solid #1e73be" : "2px solid #e2e8f0",
                            background: isSelected
                              ? "linear-gradient(135deg, #1e73be 0%, #3EBBF0 100%)"
                              : "#ffffff",
                            color: isSelected ? "#ffffff" : "#64748b",
                            fontWeight: isSelected ? 700 : 500,
                            fontSize: "0.8rem",
                            cursor: "pointer",
                            transition: "all 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                            transform: isSelected ? "scale(1.08)" : "scale(1)",
                            boxShadow: isSelected ? "0 3px 10px rgba(30, 115, 190, 0.3)" : "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {dayLabel}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Dispatch Time */}
              <div>
                <label style={{ fontSize: "0.8rem", fontWeight: 600, color: "#64748b", marginBottom: "8px", display: "block" }}>
                  Dispatch Time
                </label>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <select
                    value={sendHour}
                    onChange={(e) => setSendHour(Number(e.target.value))}
                    className="form-input"
                    style={{ flex: 1, background: "#f8fafc" }}
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>{String(i).padStart(2, "0")}</option>
                    ))}
                  </select>
                  <span style={{ fontWeight: 700, color: "#94a3b8" }}>:</span>
                  <select
                    value={sendMinute}
                    onChange={(e) => setSendMinute(Number(e.target.value))}
                    className="form-input"
                    style={{ flex: 1, background: "#f8fafc" }}
                  >
                    {[0, 15, 30, 45].map((m) => (
                      <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Smart Schedule Summary */}
              <div style={{
                padding: "10px 14px",
                background: "rgba(30, 115, 190, 0.04)",
                borderRadius: "8px",
                border: "1px solid rgba(30, 115, 190, 0.1)",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
                <CalendarDays size={14} color="#1e73be" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: "0.8rem", color: "#475569", lineHeight: 1.4 }}>
                  {(() => {
                    const time = `${String(sendHour).padStart(2, '0')}:${String(sendMinute).padStart(2, '0')}`;
                    switch (deliveryFrequency) {
                      case 'daily':
                        return `Dispatched every day at ${time}`;
                      case 'weekdays':
                        return `Dispatched Monday through Friday at ${time}`;
                      case 'weekly':
                        return `Dispatched every ${DAY_NAMES[deliveryDays[0]] || 'Monday'} at ${time}`;
                      case 'custom': {
                        const dayNames = deliveryDays
                          .sort((a, b) => a - b)
                          .map((d) => DAY_SHORT[d])
                          .join(', ');
                        return `Dispatched on ${dayNames || 'no days selected'} at ${time}`;
                      }
                    }
                  })()}
                </span>
              </div>
            </div>
          </div>

          {/* Engine Status Panel */}
          <div style={{
            background: isEnabled ? "linear-gradient(to bottom right, #f0fdf4, #ffffff)" : "linear-gradient(to bottom right, #fef2f2, #ffffff)",
            border: `1px solid ${isEnabled ? "#bbf7d0" : "#fecaca"}`,
            borderRadius: "12px",
            padding: "20px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between"
          }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                <div style={{
                  width: "10px", height: "10px", borderRadius: "50%",
                  background: isEnabled ? "#10b981" : "#ef4444",
                  boxShadow: isEnabled ? "0 0 10px #10b981" : "0 0 10px #ef4444"
                }} />
                <h3 style={{ fontSize: "1rem", fontWeight: 600, color: "#0f172a", margin: 0 }}>Engine Status</h3>
              </div>
              <p style={{ fontSize: "0.875rem", color: "#64748b", lineHeight: 1.5, marginBottom: "24px" }}>
                {isEnabled 
                  ? "The automated briefing engine is currently active. Newsletters will be generated and dispatched according to the schedule."
                  : "The automated briefing engine is paused. No newsletters will be sent until it is reactivated."}
              </p>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px", background: "#ffffff", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
              <span style={{ fontWeight: 600, color: isEnabled ? "#10b981" : "#ef4444", fontSize: "0.875rem" }}>
                {isEnabled ? "System Active" : "System Paused"}
              </span>
              <ToggleSwitch checked={isEnabled} onChange={() => setIsEnabled(!isEnabled)} />
            </div>
          </div>
        </div>

        {/* Content Configuration */}
        <div>
          <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#0f172a", marginBottom: "8px" }}>
            Content Modules
          </h3>
          <p style={{ fontSize: "0.875rem", color: "#64748b", marginBottom: "20px" }}>
            Select which modules to include in your briefing. Sections requiring special access are locked if you don't have the required permission.
          </p>
          
          <div style={{ display: "grid", gap: "12px", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))" }}>
            {sections.map((section) => {
              const isOn = enabledSections.includes(section.key);
              // Check if the user has the required permission for this section
              const hasPermission = !section.required_permission || 
                !!(profile as unknown as Record<string, unknown>)?.[section.required_permission];
              const isLocked = !hasPermission;

              return (
                <div
                  key={section.id}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "16px",
                    padding: "16px",
                    background: isLocked ? "#fef2f2" : isOn ? "#ffffff" : "#f8fafc",
                    borderRadius: "10px",
                    border: `1px solid ${isLocked ? "#fecaca" : isOn ? "rgba(30, 115, 190, 0.2)" : "#e2e8f0"}`,
                    boxShadow: isOn && !isLocked ? "0 4px 12px rgba(30, 115, 190, 0.05)" : "none",
                    transition: "all 0.2s ease",
                    opacity: isLocked ? 0.6 : isOn ? 1 : 0.8,
                  }}
                >
                  <div style={{ 
                    fontSize: "1.5rem", 
                    width: "40px", 
                    height: "40px", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    background: isLocked ? "#fee2e2" : isOn ? "rgba(30, 115, 190, 0.08)" : "#f1f5f9",
                    borderRadius: "10px",
                    flexShrink: 0
                  }}>
                    {section.icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "4px" }}>
                      <span style={{ fontWeight: 600, fontSize: "0.95rem", color: "#0f172a" }}>
                        {section.label}
                      </span>
                      {isLocked && (
                        <span style={{
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          color: "#ef4444",
                          background: "rgba(239, 68, 68, 0.1)",
                          padding: "2px 6px",
                          borderRadius: "12px",
                        }}>
                          🔒 No Access
                        </span>
                      )}
                      {!isLocked && section.required_permission && (
                        <span style={{
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          color: "#1e73be",
                          background: "rgba(30, 115, 190, 0.1)",
                          padding: "2px 6px",
                          borderRadius: "12px",
                          letterSpacing: "0.02em"
                        }}>
                          {section.required_permission.replace("can_view_", "")}
                        </span>
                      )}
                    </div>
                    {section.description && (
                      <p style={{ fontSize: "0.8rem", color: "#64748b", margin: 0, lineHeight: 1.4 }}>
                        {section.description}
                      </p>
                    )}
                  </div>
                  <div style={{ flexShrink: 0, paddingTop: "4px" }}>
                    <ToggleSwitch 
                      checked={isOn} 
                      onChange={() => toggleSection(section.key)} 
                      disabled={isLocked}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* AI Research Prompts */}
        <div style={{
          background: "linear-gradient(to right, #f8fafc, #ffffff)",
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
          padding: "24px",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
            <div style={{ padding: "6px", background: "rgba(168, 85, 247, 0.1)", borderRadius: "8px" }}>
              <Zap size={18} color="#a855f7" />
            </div>
            <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#0f172a", margin: 0 }}>
              AI Research Directives
            </h3>
          </div>
          <p style={{ fontSize: "0.875rem", color: "#64748b", marginBottom: "16px", maxWidth: "800px" }}>
            Provide custom context for the Perplexity AI agent. These directives guide the autonomous research engine when synthesizing industry news and market analysis.
          </p>
          <textarea
            value={customTopics}
            onChange={(e) => setCustomTopics(e.target.value)}
            placeholder="e.g. Focus on structural engineering breakthroughs, high-rise concrete methodologies, and supply chain disruptions..."
            className="form-input"
            rows={4}
            style={{ 
              resize: "vertical", 
              background: "#ffffff", 
              fontSize: "0.875rem",
              lineHeight: 1.6,
              boxShadow: "inset 0 2px 4px rgba(0,0,0,0.02)"
            }}
          />
        </div>

        {/* Action Bar */}
        <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: "16px", borderTop: "1px solid #e2e8f0" }}>
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="btn btn-primary"
            style={{
              padding: "12px 32px",
              fontSize: "0.95rem",
              fontWeight: 600,
              borderRadius: "8px",
              boxShadow: hasChanges ? "0 4px 12px rgba(30, 115, 190, 0.2)" : "none",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              opacity: !hasChanges && !saving ? 0.5 : 1,
              cursor: !hasChanges && !saving ? "not-allowed" : "pointer",
              transition: "all 0.25s ease",
            }}
          >
            {saving ? <Loader2 className="spinning" size={18} /> : <Save size={18} />}
            <span>{saving ? "Persisting Configuration..." : hasChanges ? "Save Configuration" : "No Changes"}</span>
          </button>
        </div>

        {/* History Log */}
        <div style={{ marginTop: "16px" }}>
          <button
            onClick={() => {
              setShowHistory(!showHistory);
              if (!showHistory) fetchLogs();
            }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              padding: "16px",
              cursor: "pointer",
              transition: "background 0.2s ease"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px", color: "#334155", fontWeight: 600 }}>
              <History size={18} />
              <span>Dispatch Logs</span>
            </div>
            {showHistory ? <ChevronUp size={18} color="#64748b" /> : <ChevronDown size={18} color="#64748b" />}
          </button>

          {showHistory && (
            <div style={{ 
              marginTop: "12px", 
              border: "1px solid #e2e8f0", 
              borderRadius: "8px", 
              overflow: "hidden",
              background: "#ffffff"
            }}>
              {logsLoading ? (
                <div style={{ padding: "32px", textAlign: "center", color: "#64748b" }}>
                  <Loader2 className="spinning" size={24} style={{ margin: "0 auto 8px" }} />
                  <p style={{ margin: 0, fontSize: "0.85rem" }}>Loading dispatch history…</p>
                </div>
              ) : logs.length > 0 ? (
                <div style={{ maxHeight: "300px", overflow: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                    <thead style={{ position: "sticky", top: 0, background: "#f8fafc", zIndex: 1, boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}>
                      <tr>
                        <th style={{ padding: "12px 16px", fontSize: "0.75rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Timestamp</th>
                        <th style={{ padding: "12px 16px", fontSize: "0.75rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Recipient</th>
                        <th style={{ padding: "12px 16px", fontSize: "0.75rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Status</th>
                        <th style={{ padding: "12px 16px", fontSize: "0.75rem", fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Content Mode</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr key={log.id} style={{ borderBottom: "1px solid #f1f5f9", transition: "background 0.15s ease", cursor: "default" }}>
                          <td data-seq-private="" style={{ padding: "12px 16px", fontSize: "0.85rem", color: "#334155", whiteSpace: "nowrap" }}>
                            {new Date(log.sent_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}
                          </td>
                          <td style={{ padding: "12px 16px", fontSize: "0.85rem", color: "#0f172a", fontWeight: 500 }}>
                            {log.email}
                          </td>
                          <td style={{ padding: "12px 16px" }}>
                            <div style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              padding: "4px 10px",
                              borderRadius: "12px",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              background: log.status === "sent" ? "#ecfdf5" : "#fef2f2",
                              color: log.status === "sent" ? "#10b981" : "#ef4444",
                              border: `1px solid ${log.status === "sent" ? "#a7f3d0" : "#fecaca"}`
                            }}>
                              {log.status === "sent" ? <Check size={12} /> : <X size={12} />}
                              {log.status}
                            </div>
                          </td>
                          <td style={{ padding: "12px 16px", fontSize: "0.85rem", color: "#64748b" }}>
                            {log.content_mode === "monthly_summary" ? (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>📅 Monthly Sync</span>
                            ) : (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>⚡ Daily Intel</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ padding: "40px", textAlign: "center", color: "#94a3b8" }}>
                  <Newspaper size={32} style={{ margin: "0 auto 12px", opacity: 0.5 }} />
                  <p style={{ margin: 0, fontSize: "0.95rem" }}>No dispatch history available.</p>
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
